import React from 'react';
import useStyle from './panier.style';
import { ProductLinePanier } from '../../molecules/Panier/ProductLinePanier';
import { ProductLinePanierVariant } from '../../molecules/Panier/ProductLinePanierVariant';
import { CartHeader } from '../../molecules/Panier/CartHeader';
import { CartFooter } from '../../molecules/Panier/CartFooter';
import { Divider } from '../../atoms/Dividers/Divider';
import {
  sanitizeCalculToString,
  sanitizeCalul
} from '../../../helpers/number/sanitizeCalul';
import { classNames } from '../../../helpers/style/classNames';
import { PanierState } from '../../../types/panier/panier.def';
import { ID } from '../../../types/typeDef';
import { ButtonWithText } from '../../atoms/Buttons/ButtonWithText';
import { ContainerTwoThirds } from '../../layouts/ContainerTwoThirds';
import emojis from '../../../theme/emoji';
import { HeaderText } from '../../atoms/Texts/HeaderText';
import isCommandEnabled from '../../../domain/panier/isCommandEnabled';
import { getRebondDisabledMessage } from '../../../domain/panier/productRebond';

type CartVariant = 'recap' | 'panier' | 'reporting';

interface PanierViewProps {
  panier: PanierState;
  cartVariant: CartVariant;
  isPanierPage: boolean;
  onClickCommander: () => void;
  onClickModifier: () => void;
  onClickViewMore: ({ idProduit }: { idProduit: ID }) => void;
  onClickTrash: (lineToRemove: number, totalLine: number) => void;
  removeAll: () => void;
  isPublicShop: boolean;
  goToReporting: () => void;
  updateQuantity: (productId: string, quantity: number) => void;
}

export const PanierView = ({
  panier,
  cartVariant,
  onClickCommander,
  onClickModifier,
  isPanierPage = false,
  onClickViewMore,
  onClickTrash,
  removeAll,
  isPublicShop,
  goToReporting,
  updateQuantity
}: PanierViewProps) => {
  const classes = useStyle();

  return !isPanierPage ? (
    <div
      className={classNames([], {
        condition: isPanierPage,
        classe: classes.flexPage
      })}
    >
      <div
        className={classNames([], {
          condition: isPanierPage,
          classe: classes.rootFlex
        })}
      >
        <div className={classes.root}>
          <CartHeader isPanierPage={isPanierPage} />
          <Divider />
          {panier?.commandeLines
            ?.filter(line => line?.produit)
            .map((line, index: number) => (
              <div key={index}>
                {isPanierPage ? (
                  <ProductLinePanierVariant
                    line={line}
                    onClickViewMore={() =>
                      onClickViewMore({ idProduit: line.produit.id })
                    }
                    onClickTrash={() =>
                      onClickTrash(index, sanitizeCalul(line.totalLine))
                    }
                    updateCartAction={updateQuantity}
                  />
                ) : (
                  <ProductLinePanier
                    line={line}
                    onClickViewMore={() =>
                      onClickViewMore({ idProduit: line.produit.id })
                    }
                    onClickTrash={() =>
                      onClickTrash(index, sanitizeCalul(line.totalLine))
                    }
                  />
                )}
                <Divider />
              </div>
            ))}
          {!isPanierPage && panier?.commandeLines && (
            <>
              <CartFooter
                variant={cartVariant}
                nbProducts={panier?.commandeLines?.length}
                totalPanier={sanitizeCalculToString(
                  panier.totalPanier +
                    panier.fraisPort?.montant +
                    panier.contributionOng.amount
                )}
                fraisDePort={
                  panier.fraisPort?.applicable &&
                  sanitizeCalculToString(panier.fraisPort?.montant)
                }
                onClickCommander={onClickCommander}
                onClickModif={onClickModifier}
                isOrderButtonDisable={!isCommandEnabled(panier?.commandeLines)}
                ngoContribution={panier.contributionOng}
              />
              {!isCommandEnabled(panier?.commandeLines) && (
                <p className={classes.disableMessage}>
                  {getRebondDisabledMessage(
                    panier?.commandeLines,
                    isPublicShop
                  )}
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  ) : (
    <ContainerTwoThirds
      col1={
        <div className={classes.rootFlex}>
          <HeaderText text="Liste du panier" emoji={emojis['ShoppingBags']} />
          <div className={classes.root}>
            <CartHeader isPanierPage={isPanierPage} />
            <Divider />
            {panier?.commandeLines
              ?.filter(line => line?.produit)
              .map((line, index) => (
                <div key={index}>
                  {isPanierPage ? (
                    <ProductLinePanierVariant
                      line={line}
                      onClickViewMore={() =>
                        onClickViewMore({
                          idProduit: line.produit.id
                        })
                      }
                      onClickTrash={() =>
                        onClickTrash(index, sanitizeCalul(line.totalLine))
                      }
                      updateCartAction={updateQuantity}
                    />
                  ) : (
                    <ProductLinePanier
                      line={line}
                      onClickViewMore={() =>
                        onClickViewMore({
                          idProduit: line.produit.id
                        })
                      }
                      onClickTrash={() =>
                        onClickTrash(index, sanitizeCalul(line.totalLine))
                      }
                    />
                  )}
                  <Divider />
                </div>
              ))}
            {!isPanierPage && panier?.commandeLines && (
              <CartFooter
                variant={cartVariant}
                nbProducts={panier?.commandeLines?.length}
                totalPanier={sanitizeCalculToString(
                  panier.totalPanier +
                    panier.fraisPort?.montant +
                    panier.contributionOng.amount
                )}
                fraisDePort={
                  panier.fraisPort?.applicable &&
                  sanitizeCalculToString(panier.fraisPort?.montant)
                }
                onClickCommander={onClickCommander}
                onClickModif={onClickModifier}
                isOrderButtonDisable={!isCommandEnabled(panier?.commandeLines)}
                ngoContribution={panier.contributionOng}
              />
            )}
          </div>
          <div className={classes.buttonAction}>
            <ButtonWithText
              disabled={false}
              content={'Ajouter des produits'}
              variant={'primaryGreen'}
              onClick={goToReporting}
              icon={'Plus'}
            />
            <ButtonWithText
              disabled={panier?.commandeLines?.length === 0}
              content={'Supprimer tous les produits'}
              variant={'danger'}
              onClick={removeAll}
              icon={'XCircle'}
              iconColor={panier?.commandeLines?.length !== 0 && '#D12B3F'}
            />
          </div>
        </div>
      }
      col2={
        <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
          <HeaderText text="Récap de la commande" emoji={emojis['Package']} />
          <div className={classes.sidePanier}>
            <CartFooter
              variant={cartVariant}
              nbProducts={panier?.commandeLines?.length}
              totalPanier={sanitizeCalculToString(
                panier.totalPanier +
                  panier.fraisPort?.montant +
                  panier.contributionOng.amount
              )}
              fraisDePort={
                panier.fraisPort?.applicable &&
                sanitizeCalculToString(panier.fraisPort?.montant)
              }
              onClickCommander={onClickCommander}
              onClickModif={onClickModifier}
              isOrderButtonDisable={!isCommandEnabled(panier?.commandeLines)}
              ngoContribution={panier.contributionOng}
            />
            {!isCommandEnabled(panier?.commandeLines) && (
              <p className={classes.disableMessage}>
                {getRebondDisabledMessage(panier?.commandeLines, isPublicShop)}
              </p>
            )}
          </div>
        </div>
      }
    />
  );
};
